import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Box, FormControl, MenuItem, Paper, Select, SelectChangeEvent, Stack, TextField, Typography } from "@mui/material";
import Grid from '@mui/material/Grid2';

import { useContext, useEffect, useMemo, useState } from "react";
import { CurrencyContext } from "../../ConfigurationContext";

const getCurrencyFlag = (currencyCode: string): string => {
  const lowerCode = currencyCode.toUpperCase();
  return import.meta.env.DEV 
    ? `/src/assets/currency/${lowerCode}.svg` 
    : `/resources/assets/flags/${lowerCode}.svg`;
};
const Currency = () => {

  const { exchangeRates } =  useContext(CurrencyContext);
  const availableCurrencies = useMemo(() => {
    return Object.keys(exchangeRates).map(code => ({
      code,
      flag: getCurrencyFlag(code),
    }));
  }, [exchangeRates]);
  const [fromCurrency, setFromCurrency] = useState<string>("");
  const [toCurrency, setToCurrency] = useState<string>("");
  const [toAmount, setToAmount] = useState(0.00);
  const [fromAmount, setFromAmount] = useState(0.00);
  const [fee, setFee] = useState(0);
  const [amountAfterFee, setAmountAfterFee] = useState(0.00);

  const currentRate = exchangeRates[fromCurrency] && exchangeRates[fromCurrency][toCurrency] 
  ? exchangeRates[fromCurrency][toCurrency]
  : getRandomExchangeRate(exchangeRates);

    function getRandomExchangeRate(rates: Record<string, Record<string, number>>): number {
      for (const fromCode in rates) {
        for (const toCode in rates[fromCode]) {
          if (rates[fromCode][toCode] > 0) {
            return rates[fromCode][toCode];
          }
        }
      }
      return 1.0;
    }


    useEffect(() => {
      if (availableCurrencies.length > 0) {
        // Check available currencies first
        const hasEUR = availableCurrencies.some(c => c.code === "EUR");
        const hasUSD = availableCurrencies.some(c => c.code === "USD");
        
        // Set fromCurrency (only if it hasn't been set already)
        if (fromCurrency === "") {
          // Try EUR first, then USD, then fall back to first available
          if (hasEUR) {
            setFromCurrency("EUR");
          } else if (hasUSD) {
            setFromCurrency("USD");
          } else {
            setFromCurrency(availableCurrencies[0].code);
          }
        }
        
        // Set toCurrency after fromCurrency is set
        if (toCurrency === "") {
          // We'll set this in the other useEffect that depends on fromCurrency
        }
      }
    // Remove toCurrency and fromCurrency from dependencies to prevent loops
    }, [availableCurrencies]);

  useEffect(() => {
    const calculatedFee = fromAmount * 0.00175;
    setFee(calculatedFee);

    const calculatedAmountAfterFee = fromAmount - calculatedFee;
    setAmountAfterFee(calculatedAmountAfterFee);

    const rate = currentRate;
    
    const calculatedToAmount = calculatedAmountAfterFee * rate;
    setToAmount(calculatedToAmount);
  }, [fromAmount, fromCurrency, toCurrency, exchangeRates]);

  const availableFromCurrencies = useMemo(() => {
    return availableCurrencies; 
  }, [availableCurrencies]);

useEffect(() => {
  const isCurrentFromValid = availableFromCurrencies.some(c => c.code === fromCurrency);
  
  if (!isCurrentFromValid && availableFromCurrencies.length > 0) {

    setFromCurrency(availableFromCurrencies[0].code);
  }
}, [toCurrency, availableFromCurrencies]);

  const availableToCurrencies = useMemo(() => {
    if (!fromCurrency || !exchangeRates[fromCurrency]) {
      return [];
    }
    
    return Object.entries(exchangeRates[fromCurrency])
      .filter(([code, rate]) => 
        code !== fromCurrency && 
        rate !== undefined && 
        rate !== null && 
        rate > 0
      )
      .map(([code]) => ({
        code,
        flag: getCurrencyFlag(code),
      }));
  }, [fromCurrency, exchangeRates]);
  
  useEffect(() => {
    const isCurrentToValid = availableToCurrencies.some(c => c.code === toCurrency);
    
    if (!isCurrentToValid && availableToCurrencies.length > 0) {
      setToCurrency(availableToCurrencies[0].code);
    }
  }, [fromCurrency, availableToCurrencies]);


  const formatCurrency = (value: number, decimals = 2) => {
    if (!isFinite(value)) return '0.00';

    if (value >= 100000000) {
      return new Intl.NumberFormat('en-US', {
        notation: 'compact',
        maximumFractionDigits: decimals
      }).format(value);
    }
    
    return value.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };
  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(event.target.value.replace(/,/g, '')) || 0;
    setFromAmount(value);
  };

  const handleFromCurrencyChange = (event: SelectChangeEvent<string>) => {
    const newFromCurrency = event.target.value as string;
    setFromCurrency(newFromCurrency);
    
    const hasValidRate = exchangeRates[newFromCurrency]?.[toCurrency] !== undefined &&
                        exchangeRates[newFromCurrency]?.[toCurrency] > 0;
    
    if (!hasValidRate && exchangeRates[newFromCurrency]) {
      const validToCurrencies = Object.entries(exchangeRates[newFromCurrency])
        .filter(([code, rate]) => 
          code !== newFromCurrency && rate !== undefined && rate !== null && rate > 0
        );
      
      if (validToCurrencies.length > 0) {
        setToCurrency(validToCurrencies[0][0]);
      }
    }
  };

  const handleToCurrencyChange = (event: SelectChangeEvent<string>) => {
    setToCurrency(event.target.value as string);
  };




  return (
    <Grid
      container
      spacing={4}
      sx={{ maxWidth: "1320px", 
        maxHeight: {md: "626px", xs: "674px"}, 
        alignItems: "center",
      margin: "0 auto",}}
    >
      {/* Left side - Currency converter */}
      <Grid size={{xs: 12, md: 7}} sx={{ 
        maxHeight: {md: "546px", xs:" 500px"},
        order: {xs: 2, md: 1}
        }}>
        <Paper
          elevation={0}
          sx={{
            bgcolor: "lightPurple.100",
            borderRadius: 4,
            p: 3,
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          <Stack spacing={3} width="100%">
            {/* From currency section  */}
            <Box
              sx={{
                bgcolor: "rgb(236, 233, 254)",
                borderRadius: "14px",
                px: 2.5,
                py: 0,
                height: "68px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                // maxWidth: {md: "541px", xs: "315px"},
                overflow: 'hidden'
              }}
            >
              <Box>
                <Typography
                  variant="body1"
                  sx={{
                    color: "darkPurple.100",
                    display: "block",
                  }}
                >
                  CONVERT
                </Typography>
                <TextField
                  variant="standard"
                  value={formatCurrency(fromAmount, 0.00)}
                  onChange={handleAmountChange}
                  inputMode="decimal"
                  InputProps={{
                    disableUnderline: true,
                    sx: { 
                      fontSize: {md: "28px", xs: "20px"},
                      color: "darkPurple.500",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "100%"
                    }
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  width: "200px",
                  justifyContent: "flex-end",
                }}
              >
                <FormControl variant="standard">
                  <Select
                    value={fromCurrency}
                    onChange={handleFromCurrencyChange}
                    disableUnderline
                    IconComponent={KeyboardArrowDownIcon}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      "& .MuiMenuItem-root:hover": {
                                backgroundColor: "lightblue", // Custom hover color
                              },
                      gap: { xs: 0.5, md: 1 },
                      '& .MuiSelect-select': {
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        paddingRight: 4,
                        color: "#321338",
                        fontWeight: 500,
                      }
                    }}
                    MenuProps={{
                      sx: {
                        "& .MuiPaper-root": {
                          backgroundColor: "white", // Dropdown background color
                        },
                        "& .MuiMenuItem-root:hover": {
                          backgroundColor: "lightblue", // Custom hover color for items
                        },
                      },
                    }}
                    renderValue={(selected) => {
                      const currency = availableFromCurrencies.find(c => c.code === selected);
                      return (
                        <>
                          <Box
                            sx={{
                              width: {md: 40, xs: 36},
                              height: {md: 40, xs: 36},
                              bgcolor: "lightPurple.300",
                              borderRadius: {md: "14px", xs: "12.6px"},
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Box
                              component="img"
                              src={currency?.flag}
                              alt="Currency flag"
                              sx={{
                                width: {md: "22px", xs: "20px"},
                                height: {md: "16px", xs: "14px"}
                              }}
                            />
                          </Box>
                          <Typography variant="subtitle3">
                            {selected}
                          </Typography>
                        </>
                      );
                    }}
                  >
                    {availableFromCurrencies.map((currency) => (
                      <MenuItem key={currency.code} value={currency.code} sx={{
                        display: 'flex', 
                        alignItems: 'center', 
                        alignSelf: 'stretch',
                        gap: "8px",
                        padding: "0px 16px",
                        minHeight: "40px",
                        borderRadius: "8px",
                        minWidth:"160px"
                      }}>
                          <Box
                            component="img" 
                            src={currency.flag} 
                            alt={`${currency.code} flag`}
                            style={{ width: "20px", height: "14px" }}
                          />
                          <Typography variant="body7" sx={{
                            display: "flex",
                            height: "40px",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "flex-start",
                            gap: "0px",
                            flex: "1 0 0"
                          }}>{currency.code}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* Conversion details */}
            <Stack spacing={1} width="100%">
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      bgcolor: "lightPurple.400",
                      borderRadius: 1,
                    }}
                  />
                  <Typography variant="body5" sx={{ color: "darkPurple.500" }}>
                    {formatCurrency(fee)} {fromCurrency}
                  </Typography>
                </Box>
                <Typography variant="body5" sx={{ color: "darkPurple.500" }}>
                  OUR FEE
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      bgcolor: "lightPurple.400",
                      borderRadius: 1,
                    }}
                  />
                  <Typography variant="body5" sx={{ color: "darkPurple.500" }}>
                    {formatCurrency(amountAfterFee)} {fromCurrency}
                  </Typography>
                </Box>
                <Typography variant="body5" sx={{ color: "darkPurple.500" }}>
                  AMOUNT WE'LL CONVERT
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      bgcolor: "lightPurple.400",
                      borderRadius: 1,
                    }}
                  />
                  <Typography variant="body5" sx={{ color: "darkPurple.500" }}>
                    {currentRate.toFixed(2)}
                  </Typography>
                </Box>
                <Typography variant="body5" sx={{ color: "darkPurple.500" }}>
                  RATE
                </Typography>
              </Box>
            </Stack>

            {/* To currency section  */}
            <Box
              sx={{
                bgcolor: "#9584BA",
                borderRadius: "14px",
                px: 2.5,
                py: 0,
                height: "68px",
                display: "flex",
                alignItems: "center",
                alignSelf: "stretch",
                justifyContent: "space-between",
                // maxWidth: {md: "541px", xs: "315px"},
                overflow: 'hidden'
              }}
            >
              <Box>
                <Typography
                  variant="body1"
                  sx={{
                    color: "lightPurple.300",
                    fontWeight: 500,
                    display: "block",
                  }}
                >
                  TO
                </Typography>
                <Typography
                  variant="subtitle4"
                  sx={{
                    color: "beige.100",
                    fontWeight: 500,
                    overflow: 'hidden'
                  }}
                >
                  {formatCurrency(toAmount)}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  justifyContent: "flex-end",
                  
                }}
              >
                <FormControl variant="standard">
                  <Select
                    value={toCurrency}
                    onChange={handleToCurrencyChange}
                    disableUnderline
                    IconComponent={KeyboardArrowDownIcon}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      '& .MuiSelect-icon': { color: 'beige.100' },
                      "& .MuiMenuItem-root:hover": {
                        backgroundColor: "lightblue", // Custom hover color
                      },
                      '& .MuiSelect-select': {
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        paddingRight: 4,
                        color: "#F6F4FF",
                        fontWeight: 500,
                      }
                    }}
                    MenuProps={{
                      sx: {
                        "& .MuiPaper-root": {
                          backgroundColor: "white", // Dropdown background color
                        },
                        "& .MuiMenuItem-root:hover": {
                          backgroundColor: "lightblue", // Custom hover color for items
                        },
                      },
                    }}
                    renderValue={(selected) => {
                      const currency = availableToCurrencies.find(c => c.code === selected);
                      return (
                        <>
                          <Box
                            sx={{
                              width: {md: 40, xs: 36},
                              height: {md: 40, xs: 36},
                              bgcolor: "lightPurple.300",
                              borderRadius: {md: "14px", xs: "12.6px"},
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Box
                              component="img"
                              src={currency?.flag}
                              alt="Currency flag"
                              sx={{
                                width: {md: "22px", xs: "20px"},
                                height: {md: "16px", xs: "14px"}
                              }}
                            />
                          </Box>
                          <Typography variant="subtitle3">
                            {selected}
                          </Typography>
                        </>
                      );
                    }}
                  >
                    {availableToCurrencies.map((currency) => (
                      <MenuItem key={currency.code} value={currency.code} sx={{
                        display: 'flex', 
                        alignItems: 'center', 
                        alignSelf: 'stretch',
                        gap: "8px",
                        padding: "0px 16px",
                        minHeight: "40px",
                        borderRadius: "8px",
                        minWidth:"160px"
                      }}>
                          <Box
                            component="img" 
                            src={currency.flag} 
                            alt={`${currency.code} flag`}
                            style={{ width: "20px", height: "14px" }}
                          />
                          <Typography variant="body7" sx={{
                            display: "flex",
                            height: "40px",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "flex-start",
                            gap: "0px",
                            flex: "1 0 0"
                          }}>{currency.code}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </Stack>

          {/* Fee section */}
          <Box
            sx={{
              bgcolor: "rgb(236, 233, 254)",
              borderRadius: "14px",
              px: 2.5,
              width: "100%",
            }}
          >
            <Box
              sx={{
                height: "68px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: "darkPurple.500",
                }}
              >
                OUR FEE
              </Typography>
              <Typography
                variant="subtitle4"
                sx={{
                  color: "lightPurple.500",
                }}
              >
                {formatCurrency(fee)} {fromCurrency}
              </Typography>
            </Box>
          </Box>

          {/* Mark-up info */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Typography
              sx={{
                color: "#321338",
                fontWeight: 500,
                lineHeight: 1.5,
              }}
            >
              0.175% MARK-UP AGAINST THE LATEST
              <br />
              AVAILABLE RATE PUBLISHED BY THE ECB
            </Typography>
            <Box
              sx={{
                width: 24,
                height: 24,
                bgcolor: "lightPurple.400",
                borderRadius: 1,
              }}
            />
          </Box>
        </Paper>
      </Grid>

      {/* Right side - Description (unchanged) */}
      <Grid size={{xs: 12, md: 5}} sx={{
        display: "flex",
        flexDirection: "column",  
        flexWrap: "wrap",
        justifyContent: "center",
        order: {xs: 1, md: 2}}}>
        <Stack spacing={4} sx={{
          display: "flex",
          flexDirection: "column",
          gap: {md: '32px', xs: "16px"},
          alignItems: "flex-start",
          flex: 1,
          alignSelf: "stretch",
          padding: "0px 6px",
          flexWrap: "wrap",
        }}>
          <Typography
            variant="subtitle8"
            sx={{
              textTransform: "uppercase",
              textAlign: "center",
            }}
          >
            <Box component="span" sx={{ color: "darkPurple.500" }}>
              Currency{" "}
            </Box>
            <Box component="span" sx={{ color: "lightPurple.500" }}>
              exchange
            </Box>
          </Typography>

          <Typography
            variant="body7"
            sx={{
              color: "gray.400",
              textWrap: "wrap",
              maxWidth: {xs:"100%", md: "407px"}
            }}
          >
            EASILY CONVERT YOUR FUNDS BETWEEN CURRENCIES,
            GIVING YOU FLEXIBILITY AND CONVENIENCE
            IN YOUR FINANCIAL MANAGEMENT
          </Typography>
        </Stack>
      </Grid>
    </Grid>
  );
};
export default Currency;
